/**
 * Global stream tracker to manage active media streams
 * This ensures we can properly stop streams without creating new ones
 */

class StreamTracker {
  private activeStreams: Set<MediaStream> = new Set();

  /**
   * Add a stream to tracking
   */
  addStream(stream: MediaStream): void {
    this.activeStreams.add(stream);
    console.log(`Stream added to tracker. Total streams: ${this.activeStreams.size}`);
  }

  /**
   * Remove a stream from tracking
   */
  removeStream(stream: MediaStream): void {
    this.activeStreams.delete(stream);
    console.log(`Stream removed from tracker. Total streams: ${this.activeStreams.size}`);
  }

  /**
   * Stop all tracked streams
   */
  stopAllStreams(): void {
    console.log(`Stopping ${this.activeStreams.size} tracked streams...`);
    
    this.activeStreams.forEach(stream => {
      stream.getTracks().forEach(track => {
        if (track.readyState === 'live') {
          console.log(`Stopping ${track.kind} track:`, track.label);
          track.stop();
        }
      });
    });

    this.activeStreams.clear();
    console.log('All tracked streams stopped');
  }

  /**
   * Get count of active streams
   */
  getActiveStreamCount(): number {
    return this.activeStreams.size;
  }

  /**
   * Check if any streams are active
   */
  hasActiveStreams(): boolean {
    return this.activeStreams.size > 0;
  }
}

// Global instance
export const streamTracker = new StreamTracker();

/**
 * Hook to track a media stream
 */
export function useStreamTracker() {
  const addStream = (stream: MediaStream) => streamTracker.addStream(stream);
  const removeStream = (stream: MediaStream) => streamTracker.removeStream(stream);
  const stopAllStreams = () => streamTracker.stopAllStreams();
  const getActiveStreamCount = () => streamTracker.getActiveStreamCount();
  const hasActiveStreams = () => streamTracker.hasActiveStreams();

  return {
    addStream,
    removeStream,
    stopAllStreams,
    getActiveStreamCount,
    hasActiveStreams
  };
}
